import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Check, Clipboard, Loader2, Palette } from 'lucide-react'
import { copyToClipboard } from '@/frontend/utils/clipboard'
import type { StationzeroEmailAccountStatus, WorkerFormData } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  form: WorkerFormData
  stationzeroEmailAccountStatus: StationzeroEmailAccountStatus
  workerWhatsApp: string
  canManage: boolean
  onSave: () => void
  onOpenCustomize?: () => void
}

interface CopyableFieldProps {
  label: string
  value: string
  fieldKey: string
  copiedField: string | null
  onCopy: (value: string, fieldKey: string) => void
  emptyLabel?: string
}

function CopyableField({ label, value, fieldKey, copiedField, onCopy, emptyLabel = '—' }: CopyableFieldProps) {
  const isEmpty = !value
  const isCopied = copiedField === fieldKey
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => !isEmpty && onCopy(value, fieldKey)}
        disabled={isEmpty}
        title={isEmpty ? undefined : isCopied ? 'Copiado!' : 'Copiar'}
        className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-muted/40 px-3 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground"
      >
        <span className="truncate">{isEmpty ? emptyLabel : value}</span>
        {!isEmpty && (
          isCopied
            ? <Check className="h-3.5 w-3.5 shrink-0 text-success" />
            : <Clipboard className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
      </button>
    </div>
  )
}

export default function WorkerProfileForm({ form, stationzeroEmailAccountStatus, workerWhatsApp, canManage, onSave, onOpenCustomize }: Props) {
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const emailProvisioned = stationzeroEmailAccountStatus === 'provisioned'

  const handleCopy = async (value: string, fieldKey: string) => {
    const ok = await copyToClipboard(value)
    if (ok) {
      setCopiedField(fieldKey)
      window.setTimeout(() => setCopiedField(null), 2000)
    }
  }

  return (
    // DS novo: seção plana (borda + radius-12, sem sombra) igual aos irmãos
    // Assinatura/Zona de perigo — não mais o Card elevado.
    <section className="space-y-4 rounded-xl border border-border bg-card p-5">
      <div className="flex flex-row items-center justify-between gap-3">
        <h2 className="text-[14px] font-semibold text-foreground">Dados do colaborador</h2>
        {canManage && onOpenCustomize && (
          <Button type="button" variant="outline" size="sm" onClick={onOpenCustomize}>
            <Palette className="h-3.5 w-3.5" />
            Personalizar aparência
          </Button>
        )}
      </div>
      <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-4">
              <CopyableField
                label="Nome"
                value={form.data.name}
                fieldKey="name"
                copiedField={copiedField}
                onCopy={handleCopy}
              />
              <div className="grid gap-2">
                <Label htmlFor="worker-contact-name">Responsável</Label>
                <Input id="worker-contact-name" value={form.data.contact_name} onChange={(e) => form.setData('contact_name', e.target.value)} disabled={!canManage} />
                {form.errors.contact_name && <p className="text-sm text-destructive">{form.errors.contact_name}</p>}
              </div>
            </div>
            <div className="space-y-4">
              <CopyableField
                label="Email"
                value={emailProvisioned ? form.data.stationzero_email : ''}
                fieldKey="email"
                copiedField={copiedField}
                onCopy={handleCopy}
                emptyLabel="Email não provisionado"
              />
              <CopyableField
                label="Telefone"
                value={workerWhatsApp}
                fieldKey="phone"
                copiedField={copiedField}
                onCopy={handleCopy}
                emptyLabel="Sem número"
              />
            </div>
          </div>
          {canManage && (
            <div className="flex items-center justify-end pt-2">
              <Button onClick={onSave} disabled={form.processing}>
                {form.processing && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar alterações
              </Button>
            </div>
          )}
        </div>
    </section>
  )
}
